/**
 * DataTableClientBlockRenderer
 *
 * Renders `data_table_client` blocks using the DataTable (client variant) from @strivelabs/ui-core.
 * Supports optional page navigation via ReportNavigationContext:
 * - Rows with `__page_ref` are clickable and navigate to the referenced page
 * - `__page_ref` is not rendered as a visible column (TanStack Table only renders defined columns)
 * - If no navigation context is available (e.g., flat reports, chat), rows render as normal
 *
 * To add a linked table to any page in a multi-page report:
 * 1. Backend includes a `data_table_client` block in the page's blocks array
 * 2. Each row that should link includes `__page_ref: { page_group_id, page_id }`
 * 3. No frontend changes needed — the renderer picks up `__page_ref` automatically
 */

import { useMemo, useCallback } from 'react';
import { DataTable } from '@strivelabs/ui-core';
import type { ColumnDef } from '@tanstack/react-table';
import type { DataTableClientBlock, DataTableClientRow } from '@/types/ui-blocks';
import { useReportNavigation } from '@/contexts/ReportNavigationContext';

export interface DataTableClientBlockRendererProps {
  block: DataTableClientBlock;
}

export function DataTableClientBlockRenderer({ block: { content } }: DataTableClientBlockRendererProps) {
  const navigation = useReportNavigation();

  // Build TanStack ColumnDef[] from the contract columns
  // First column of navigable rows gets hyperlink styling (text-blue-600 hover:underline)
  const columns = useMemo((): ColumnDef<DataTableClientRow>[] => {
    return content.columns.map((col, colIndex) => ({
      accessorKey: col.key,
      header: col.label,
      cell: ({ row }) => {
        const value = row.original[col.key];
        const display = value === null || value === undefined ? '—' : String(value);

        // First column + navigable row → render as hyperlink
        if (colIndex === 0 && navigation && row.original.__page_ref) {
          return <span className="text-blue-600 hover:underline">{display}</span>;
        }

        return display;
      },
    }));
  }, [content.columns, navigation]);

  const rows = content.rows;

  // Only enable row clicks when navigation context exists AND at least one row has a page ref
  const hasNavigableRows = navigation !== null && rows.some((r) => r.__page_ref);

  const handleRowClick = useCallback(
    (row: DataTableClientRow) => {
      if (navigation && row.__page_ref) {
        navigation.navigateToPage(row.__page_ref);
      }
    },
    [navigation]
  );

  return (
    <div className="flex flex-col gap-2 py-4">
      {(content.title || content.description) && (
        <div className="flex flex-col gap-1">
          {content.title && (
            <h3 className="text-sm text-custom-text-secondary font-semibold leading-tight tracking-tight">
              {content.title}
            </h3>
          )}
          {content.description && (
            <p className="text-sm text-muted-foreground">{content.description}</p>
          )}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 p-4">
        <DataTable
          variant="client"
          columns={columns}
          data={rows}
          enableSearch={content.enableSearch ?? false}
          searchPlaceholder={content.searchPlaceholder}
          fuzzySearch={content.enableSearch ?? false}
          enablePagination={content.enablePagination ?? false}
          pageSize={content.pageSize ?? 10}
          emptyText={content.emptyText ?? 'No results found'}
          bordered={false}
          onRowClick={hasNavigableRows ? handleRowClick : undefined}
        />
      </div>
    </div>
  );
}
